package com.yendo.mx.ui.profile

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.DeleteForever
import androidx.compose.material.icons.filled.WarningAmber
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.BackHandler
import kotlinx.coroutines.launch

enum class DeleteStep { WARNING, REASON, CONFIRM }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EliminarCuentaScreen(onBack: () -> Unit, onCuentaEliminada: () -> Unit) {
    var pasoActual by remember { mutableStateOf(DeleteStep.WARNING) }
    var motivoSeleccionado by remember { mutableStateOf("") }
    var otroMotivoDetalle by remember { mutableStateOf("") } // 🚀 NUEVO
    var aceptaConsecuencias by remember { mutableStateOf(false) }
    var procesando by remember { mutableStateOf(false) }
    var mensajeError by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    val isDark = isSystemInDarkTheme()
    val colorFondo = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTexto = if (isDark) Color.White else Color.Black
    val colorSecundario = if (isDark) Color.LightGray else Color.DarkGray
    
    val motivos = listOf("No uso la aplicación", "Tuve una mala experiencia", "Los precios son muy altos", "Faltan restaurantes en mi zona", "Tengo otra cuenta", "Otro motivo")

    BackHandler {
        if (procesando) return@BackHandler
        when (pasoActual) {
            DeleteStep.CONFIRM -> pasoActual = DeleteStep.REASON
            DeleteStep.REASON -> pasoActual = DeleteStep.WARNING
            DeleteStep.WARNING -> onBack()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Eliminar cuenta", fontWeight = FontWeight.Bold, color = colorTexto) },
                navigationIcon = {
                    IconButton(onClick = { 
                        if (!procesando) {
                            when (pasoActual) {
                                DeleteStep.CONFIRM -> pasoActual = DeleteStep.REASON
                                DeleteStep.REASON -> pasoActual = DeleteStep.WARNING
                                DeleteStep.WARNING -> onBack()
                            }
                        }
                    }) { Icon(Icons.AutoMirrored.Filled.ArrowBack, null, tint = colorTexto) }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondo)
            )
        },
        containerColor = colorFondo
    ) { padding ->
        AnimatedContent(
            targetState = pasoActual,
            transitionSpec = { slideInHorizontally { it } + fadeIn() togetherWith slideOutHorizontally { -it } + fadeOut() },
            label = "EliminarCuentaFlow",
            modifier = Modifier.fillMaxSize().padding(padding)
        ) { paso ->
            Column(modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState())) {
                when (paso) {
                    DeleteStep.WARNING -> {
                        Icon(Icons.Default.WarningAmber, null, tint = Color(0xFFFF9800), modifier = Modifier.size(64.dp).align(Alignment.CenterHorizontally))
                        Spacer(Modifier.height(24.dp))
                        Text("¿Estás seguro que deseas abandonar Yendo Mx?", fontWeight = FontWeight.Black, fontSize = 24.sp, color = colorTexto)
                        Spacer(Modifier.height(16.dp))
                        Text("Si eliminas tu cuenta, perderás de forma permanente:", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorSecundario)
                        Spacer(Modifier.height(8.dp))
                        Text("• Tu historial de pedidos y recibos.\n• Tus direcciones guardadas.\n• Tu acceso a promociones exclusivas.", fontSize = 15.sp, color = colorSecundario, lineHeight = 24.sp)
                        Spacer(Modifier.height(40.dp))
                        Button(onClick = { pasoActual = DeleteStep.REASON }, modifier = Modifier.fillMaxWidth().height(52.dp), colors = ButtonDefaults.buttonColors(containerColor = Color.Red), shape = RoundedCornerShape(12.dp)) { Text("Continuar con la eliminación", color = Color.White, fontWeight = FontWeight.Bold) }
                        Spacer(Modifier.height(12.dp))
                        OutlinedButton(onClick = onBack, modifier = Modifier.fillMaxWidth().height(52.dp), shape = RoundedCornerShape(12.dp)) { Text("Mejor me quedo", color = colorTexto, fontWeight = FontWeight.Bold) }
                    }
                    DeleteStep.REASON -> {
                        Text("Ayúdanos a mejorar", fontWeight = FontWeight.Black, fontSize = 24.sp, color = colorTexto)
                        Text("Por favor, indícanos por qué deseas irte:", color = colorSecundario, modifier = Modifier.padding(top = 8.dp, bottom = 24.dp))
                        motivos.forEach { motivo ->
                            Row(modifier = Modifier.fillMaxWidth().clickable { motivoSeleccionado = motivo }.padding(vertical = 12.dp), verticalAlignment = Alignment.CenterVertically) {
                                RadioButton(selected = motivoSeleccionado == motivo, onClick = { motivoSeleccionado = motivo }, colors = RadioButtonDefaults.colors(selectedColor = RosaYendo))
                                Text(motivo, fontSize = 16.sp, color = colorTexto, modifier = Modifier.padding(start = 8.dp))
                            }
                        }

                        // 🚀 CAMPO PARA "OTRO MOTIVO"
                        if (motivoSeleccionado == "Otro motivo") {
                            Spacer(Modifier.height(8.dp))
                            OutlinedTextField(
                                value = otroMotivoDetalle,
                                onValueChange = { otroMotivoDetalle = it },
                                label = { Text("Cuéntanos más...") },
                                placeholder = { Text("Escribe aquí tu motivo") },
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                                shape = RoundedCornerShape(12.dp),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = RosaYendo,
                                    unfocusedBorderColor = if (isDark) Color.DarkGray else Color.LightGray,
                                    focusedTextColor = colorTexto,
                                    unfocusedTextColor = colorTexto
                                )
                            )
                        }

                        Spacer(Modifier.height(32.dp))
                        
                        val habilitadoSiguiente = motivoSeleccionado.isNotEmpty() && 
                            (motivoSeleccionado != "Otro motivo" || otroMotivoDetalle.trim().isNotBlank())

                        Button(
                            onClick = { pasoActual = DeleteStep.CONFIRM }, 
                            enabled = habilitadoSiguiente, 
                            modifier = Modifier.fillMaxWidth().height(52.dp), 
                            colors = ButtonDefaults.buttonColors(containerColor = RosaYendo), 
                            shape = RoundedCornerShape(12.dp)
                        ) { Text("Siguiente", color = Color.White, fontWeight = FontWeight.Bold) }
                    }
                    DeleteStep.CONFIRM -> {
                        Icon(Icons.Default.DeleteForever, null, tint = Color.Red, modifier = Modifier.size(64.dp).align(Alignment.CenterHorizontally))
                        Spacer(Modifier.height(24.dp))
                        Text("Último paso", fontWeight = FontWeight.Black, fontSize = 24.sp, color = colorTexto, modifier = Modifier.align(Alignment.CenterHorizontally))
                        Spacer(Modifier.height(16.dp))
                        Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE)), shape = RoundedCornerShape(12.dp), modifier = Modifier.fillMaxWidth()) {
                            Text("Tu cuenta entrará en un periodo de desactivación de 30 días. Si no vuelves a iniciar sesión, todos tus datos serán borrados permanentemente. Durante este tiempo no podrás crear una nueva cuenta con tu mismo teléfono o correo.", color = Color.Red, fontSize = 14.sp, modifier = Modifier.padding(16.dp))
                        }
                        Spacer(Modifier.height(24.dp))
                        Row(modifier = Modifier.fillMaxWidth().clickable { aceptaConsecuencias = !aceptaConsecuencias }, verticalAlignment = Alignment.CenterVertically) {
                            Checkbox(checked = aceptaConsecuencias, onCheckedChange = { aceptaConsecuencias = it }, colors = CheckboxDefaults.colors(checkedColor = Color.Red))
                            Text("Entiendo que esta acción es irreversible después de 30 días.", color = colorTexto, fontSize = 14.sp, modifier = Modifier.padding(start = 8.dp))
                        }
                        if (mensajeError.isNotEmpty()) Text(mensajeError, color = Color.Red, fontSize = 13.sp, modifier = Modifier.padding(top = 16.dp).align(Alignment.CenterHorizontally))
                        Spacer(Modifier.height(32.dp))
                        Button(
                            enabled = aceptaConsecuencias && !procesando,
                            onClick = {
                                procesando = true
                                scope.launch {
                                    val motivoFinal = if (motivoSeleccionado == "Otro motivo") "Otro: $otroMotivoDetalle" else motivoSeleccionado
                                    val resultado = AuthRepository.solicitarEliminacionCuenta(motivoFinal)
                                    if (resultado == "OK") onCuentaEliminada() else { procesando = false; mensajeError = resultado }
                                }
                            },
                            modifier = Modifier.fillMaxWidth().height(52.dp), colors = ButtonDefaults.buttonColors(containerColor = Color.Red), shape = RoundedCornerShape(12.dp)
                        ) {
                            if (procesando) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
                            else Text("Desactivar cuenta", color = Color.White, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }
    }
}
